import { useState, useEffect } from "react";
import { T } from "./theme";
import { Plus, Trash2, Edit3, Check, X, Shield, Building2, Users, UserCheck, FileText, Ban, CheckCircle } from "lucide-react";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import PageLoading from "./components/PageLoading";
import Card from "./components/Card";
import Badge from "./components/Badge";
import Button from "./components/Button";
import Input from "./components/Input";

const ROLES = ["admin", "cxo", "gestor", "viewer"];

const ROLE_MATRIX = [
  { recurso: "Dashboard",        admin: true,  cxo: true,  gestor: true,  viewer: true  },
  { recurso: "Depoimentos",      admin: true,  cxo: true,  gestor: true,  viewer: true  },
  { recurso: "Comparativo",      admin: true,  cxo: true,  gestor: true,  viewer: false },
  { recurso: "Rankings",         admin: true,  cxo: true,  gestor: false, viewer: false },
  { recurso: "Tips CXO",         admin: true,  cxo: true,  gestor: false, viewer: false },
  { recurso: "PDSA",             admin: true,  cxo: true,  gestor: true,  viewer: false },
  { recurso: "Qualidade IA",     admin: true,  cxo: true,  gestor: false, viewer: false },
  { recurso: "Benchmarking",     admin: true,  cxo: true,  gestor: true,  viewer: false },
  { recurso: "Admin",            admin: true,  cxo: false, gestor: false, viewer: false },
  { recurso: "CRM",              admin: true,  cxo: true,  gestor: false, viewer: false },
  { recurso: "Configurações",    admin: true,  cxo: false, gestor: false, viewer: false },
];

const tableHeaderStyle = {
  padding: T.tableHeaderPadding,
  textAlign: "left",
  fontSize: T.tableHeaderFontSize,
  color: T.textLow,
  fontWeight: T.tableHeaderWeight,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  background: T.bgBase,
};

const tableCellStyle = {
  padding: T.tableCellPadding,
};

const tableRowStyle = {
  borderBottom: `1px solid ${T.border}`,
};

// ─── Usuarios Tab ─────────────────────────────────────────────────────────────
function UsuariosTab() {
  const [usuarios, setUsuarios]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState({ nome: "", email: "", password: "", role: "viewer", unidade: "" });
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState("");

  const load = () => {
    setLoading(true);
    fetch("/api/admin/usuarios", { credentials: "include" })
      .then(r => r.json()).then(d => { setUsuarios(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(load, []);

  const resetForm = () => { setForm({ nome: "", email: "", password: "", role: "viewer", unidade: "" }); setEditId(null); setShowForm(false); setErr(""); };

  const startEdit = (u) => {
    setForm({ nome: u.nome, email: u.email, password: "", role: u.role, unidade: u.unidade || "" });
    setEditId(u.id);
    setShowForm(true);
  };

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      const url = editId ? `/api/admin/usuarios/${editId}` : "/api/admin/usuarios";
      const method = editId ? "PUT" : "POST";
      const body = { ...form };
      if (!body.password) delete body.password;
      const r = await fetch(url, { method, headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Erro"); setSaving(false); return; }
      resetForm(); load();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Excluir usuário?")) return;
    await fetch(`/api/admin/usuarios/${id}`, { method: "DELETE", credentials: "include" });
    load();
  };

  const roleColor = { admin: T.accent, cxo: T.purple, gestor: T.teal, viewer: T.textLow };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: T.textMid }}>
          {loading ? "..." : `${usuarios.length} usuário(s)`}
        </span>
        <Button variant="primary" size="sm" icon={Plus} onClick={() => { resetForm(); setShowForm(true); }}
          style={{ background: T.green, boxShadow: "none" }}>
          Novo Usuário
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card style={{ marginBottom: 16, padding: "16px 20px" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.textMid, marginBottom: 12 }}>
            {editId ? "Editar Usuário" : "Novo Usuário"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { key: "nome",     label: "Nome",      type: "text"     },
              { key: "email",    label: "Email",     type: "email"    },
              { key: "password", label: editId ? "Nova Senha (opcional)" : "Senha", type: "password" },
              { key: "unidade",  label: "Unidade",   type: "text"     },
            ].map(({ key, label, type }) => (
              <Input key={key} label={label} type={type} value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}/>
            ))}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T.textMid, marginBottom: 6, fontFamily: T.fontBody }}>Role</label>
              <select style={{
                width: "100%",
                padding: T.inputPadding,
                fontSize: 14,
                fontFamily: T.fontBody,
                color: T.textHi,
                background: T.bgInput,
                border: "1px solid transparent",
                borderRadius: T.inputRadius,
                outline: "none",
              }} value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          {err && <div style={{ marginTop: 10, fontSize: 12, color: T.red }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <Button variant="primary" size="sm" icon={Check} onClick={save} disabled={saving}
              style={{ background: T.green, boxShadow: "none" }}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
            <Button variant="ghost" size="sm" icon={X} onClick={resetForm}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {/* Table */}
      {loading ? (
        <PageLoading label="Carregando..." />
      ) : (
        <Card noPadding>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Nome", "Email", "Role", "Unidade", "Status", "Ações"].map(h => (
                  <th key={h} style={tableHeaderStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usuarios.map(u => (
                <tr key={u.id} style={tableRowStyle}
                  onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ ...tableCellStyle, fontSize: 13, color: T.textHi, fontWeight: 500 }}>{u.nome}</td>
                  <td style={{ ...tableCellStyle, fontSize: 12, color: T.textMid }}>{u.email}</td>
                  <td style={tableCellStyle}>
                    <Badge color={roleColor[u.role] || T.textLow}>{u.role}</Badge>
                  </td>
                  <td style={{ ...tableCellStyle, fontSize: 12, color: T.textLow }}>{u.unidade || "—"}</td>
                  <td style={tableCellStyle}>
                    <Badge color={u.ativo !== false ? T.green : T.red}>
                      {u.ativo !== false ? "Ativo" : "Inativo"}
                    </Badge>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Button variant="ghost" size="sm" icon={Edit3} onClick={() => startEdit(u)}
                        style={{ padding: "5px 10px", color: T.accent }} />
                      <Button variant="ghost" size="sm" icon={Trash2} onClick={() => del(u.id)}
                        style={{ padding: "5px 10px", color: T.red }} />
                    </div>
                  </td>
                </tr>
              ))}
              {usuarios.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", color: T.textLow, fontSize: 13 }}>Nenhum usuário.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ─── Hospitais Tab ────────────────────────────────────────────────────────────
function HospitaisTab() {
  const [hospitais, setHospitais] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    fetch("/api/hospitais-disponiveis", { credentials: "include" })
      .then(r => r.json()).then(d => { setHospitais(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 14, color: T.textMid, fontWeight: 600 }}>
        {loading ? "..." : `${hospitais.length} hospitais na rede`}
      </div>
      {loading ? (
        <PageLoading label="Carregando..." />
      ) : (
        <Card noPadding>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["#", "Hospital", ""].map((h, i) => (
                  <th key={i} style={tableHeaderStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hospitais.map((h, i) => (
                <tr key={h} style={tableRowStyle}
                  onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ ...tableCellStyle, fontSize: 12, color: T.textLow, width: 40 }}>{i + 1}</td>
                  <td style={{ ...tableCellStyle, fontSize: 13, color: T.textMid }}>{h}</td>
                  <td style={tableCellStyle}>
                    <Badge color={T.accent}>
                      <Building2 size={10} style={{ marginRight: 4 }}/> Sua Instituição
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ─── Permissões Tab ───────────────────────────────────────────────────────────
function PermissoesTab() {
  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 14, color: T.textMid, fontWeight: 600 }}>
        Matriz de Permissões
      </div>
      <Card noPadding>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              <th style={tableHeaderStyle}>Recurso</th>
              {ROLES.map(r => (
                <th key={r} style={{ ...tableHeaderStyle, textAlign: "center" }}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROLE_MATRIX.map(row => (
              <tr key={row.recurso} style={tableRowStyle}>
                <td style={{ ...tableCellStyle, fontSize: 13, color: T.textMid }}>{row.recurso}</td>
                {ROLES.map(r => (
                  <td key={r} style={{ ...tableCellStyle, textAlign: "center" }}>
                    {row[r]
                      ? <CheckCircle size={14} color={T.green}/>
                      : <span style={{ color: T.textLow, fontSize: 14 }}>—</span>
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ─── TrueNPS Usuarios Tab ─────────────────────────────────────────────────────
function TrueNPSUsuariosTab() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading]   = useState(true);

  const load = () => {
    setLoading(true);
    fetch("/api/admin/tnps-usuarios", { credentials: "include" })
      .then(r => r.json()).then(d => { setUsuarios(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);

  const revogar = async (hash) => {
    if (!window.confirm("Revogar acesso deste usuário?")) return;
    await fetch(`/api/admin/tnps-usuarios/${hash}/revogar`, { method: "POST", credentials: "include" });
    load();
  };

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 14, color: T.textMid, fontWeight: 600 }}>
        {loading ? "..." : `${usuarios.length} usuário(s) TrueNPS`}
      </div>
      {loading ? (
        <PageLoading label="Carregando..." />
      ) : (
        <Card noPadding>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Hash", "Nome", "Último Acesso", "Hospitais Vistos", "Ações"].map(h => (
                  <th key={h} style={tableHeaderStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usuarios.map(u => (
                <tr key={u.emailHash} style={tableRowStyle}
                  onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ ...tableCellStyle, fontSize: 11, color: T.textLow, fontFamily: T.fontMono }}>{u.emailHash}</td>
                  <td style={{ ...tableCellStyle, fontSize: 13, color: T.textHi, fontWeight: 500 }}>{u.nome || "—"}</td>
                  <td style={{ ...tableCellStyle, fontSize: 12, color: T.textMid }}>{u.ultimoAcesso ? new Date(u.ultimoAcesso).toLocaleString("pt-BR") : "—"}</td>
                  <td style={{ ...tableCellStyle, fontSize: 12, color: T.textLow }}>
                    {(u.hospitaisVistos || []).length} hospital(is)
                  </td>
                  <td style={tableCellStyle}>
                    <Button variant="danger" size="sm" icon={Ban} onClick={() => revogar(u.emailHash)}
                      style={{ padding: "5px 10px" }}>
                      Revogar
                    </Button>
                  </td>
                </tr>
              ))}
              {usuarios.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: T.textLow, fontSize: 13 }}>Nenhum usuário TrueNPS registrado.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ─── Audit Log Tab ────────────────────────────────────────────────────────────
function AuditLogTab() {
  const [logs, setLogs]   = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage]   = useState(1);
  const [loading, setLoading] = useState(true);

  const load = (p = 1) => {
    setLoading(true);
    fetch(`/api/admin/audit-log?page=${p}&limit=50`, { credentials: "include" })
      .then(r => r.json()).then(d => { setLogs(d.items || []); setTotal(d.total || 0); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(page); }, [page]);

  const acaoColor = { login: T.green, sync: T.accent, relatorio: T.purple, revogar: T.red };

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 14, color: T.textMid, fontWeight: 600 }}>
        {loading ? "..." : `${total} evento(s) de auditoria`}
      </div>
      {loading ? (
        <PageLoading label="Carregando..." />
      ) : (
        <>
          <Card noPadding style={{ marginBottom: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  {["Timestamp", "Ação", "EmailHash", "Detalhes"].map(h => (
                    <th key={h} style={tableHeaderStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((l, i) => (
                  <tr key={i} style={tableRowStyle}
                    onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={{ ...tableCellStyle, fontSize: 11, color: T.textLow }}>{new Date(l.ts).toLocaleString("pt-BR")}</td>
                    <td style={tableCellStyle}>
                      <Badge color={acaoColor[l.acao] || T.textLow}>{l.acao}</Badge>
                    </td>
                    <td style={{ ...tableCellStyle, fontSize: 11, color: T.textLow, fontFamily: T.fontMono }}>{l.emailHash}</td>
                    <td style={{ ...tableCellStyle, fontSize: 11, color: T.textLow }}>
                      {l.unidade || l.by || ""}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: T.textLow, fontSize: 13 }}>Nenhum log de auditoria.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
          {total > 50 && (
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</Button>
              <span style={{ fontSize: 12, color: T.textLow, padding: "8px 12px" }}>Pág. {page} de {Math.ceil(total / 50)}</span>
              <Button variant="primary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 50)}>Próxima</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const TABS = [
  { key: "tnps-usuarios", label: "Usuários TrueNPS", icon: UserCheck  },
  { key: "usuarios",      label: "Usuários Locais",  icon: Users      },
  { key: "hospitais",     label: "Hospitais",        icon: Building2  },
  { key: "permissoes",    label: "Permissões",       icon: Shield     },
  { key: "audit",         label: "Audit Log",        icon: FileText   },
];

export default function Admin() {
  const [tab, setTab] = useState("tnps-usuarios");

  return (
    <PageContainer>
      <PageHeader
        icon={Shield}
        title="Administração"
        description="Gestão de usuários, hospitais e permissões"
      />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: `1px solid ${T.border}`, paddingBottom: 0 }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "10px 16px",
            background: "transparent", border: "none",
            borderBottom: `2px solid ${tab === key ? T.accent : "transparent"}`,
            color: tab === key ? T.accent : T.textLow,
            fontSize: 13, fontWeight: tab === key ? 600 : 400,
            cursor: "pointer", transition: T.transition,
            fontFamily: T.fontBody,
            marginBottom: -1,
          }}>
            <Icon size={14}/>
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "tnps-usuarios" && <TrueNPSUsuariosTab/>}
      {tab === "usuarios"      && <UsuariosTab/>}
      {tab === "hospitais"     && <HospitaisTab/>}
      {tab === "permissoes"    && <PermissoesTab/>}
      {tab === "audit"         && <AuditLogTab/>}
    </PageContainer>
  );
}
